import React from 'react';

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ 
  size = 'md', 
  className = '' 
}) => {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-8 w-8',
    lg: 'h-12 w-12',
    xl: 'h-16 w-16'
  };

  return (
    <div className={`animate-spin rounded-full border-b-2 border-blue-600 ${sizeClasses[size]} ${className}`} />
  );
};

interface LoadingCardProps {
  className?: string;
}

export const LoadingCard: React.FC<LoadingCardProps> = ({ className = '' }) => {
  return (
    <div className={`bg-gray-800 rounded-lg p-6 border border-gray-700 ${className}`}>
      <div className="animate-pulse">
        <div className="h-4 bg-gray-700 rounded w-3/4 mb-4"></div>
        <div className="h-3 bg-gray-700 rounded w-1/2 mb-2"></div>
        <div className="h-3 bg-gray-700 rounded w-2/3 mb-4"></div>
        <div className="h-8 bg-gray-700 rounded w-1/4"></div>
      </div>
    </div>
  );
};

interface LoadingTableRowProps {
  columns: number;
  className?: string;
}

export const LoadingTableRow: React.FC<LoadingTableRowProps> = ({ 
  columns, 
  className = '' 
}) => {
  return (
    <tr className={className}>
      {Array.from({ length: columns }).map((_, index) => (
        <td key={index} className="px-6 py-4">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-700 rounded w-3/4"></div>
          </div>
        </td>
      ))}
    </tr>
  );
};

interface LoadingPageProps {
  message?: string;
  className?: string;
}

export const LoadingPage: React.FC<LoadingPageProps> = ({ 
  message = 'Loading...', 
  className = '' 
}) => {
  return (
    <div className={`flex flex-col items-center justify-center min-h-96 ${className}`}>
      <LoadingSpinner size="xl" className="mb-4" />
      <p className="text-gray-300 text-lg">{message}</p>
    </div>
  );
};

interface SkeletonTextProps {
  lines?: number;
  className?: string;
}

export const SkeletonText: React.FC<SkeletonTextProps> = ({ 
  lines = 3, 
  className = '' 
}) => {
  return (
    <div className={`animate-pulse ${className}`}>
      {Array.from({ length: lines }).map((_, index) => (
        <div 
          key={index}
          className={`h-4 bg-gray-700 rounded mb-2 ${
            index === lines - 1 ? 'w-2/3' : 'w-full'
          }`}
        />
      ))}
    </div>
  );
};

interface SkeletonCardProps {
  className?: string;
  hasImage?: boolean;
}

export const SkeletonCard: React.FC<SkeletonCardProps> = ({ 
  className = '',
  hasImage = false 
}) => {
  return (
    <div className={`bg-gray-800 rounded-lg p-6 border border-gray-700 ${className}`}>
      <div className="animate-pulse">
        {hasImage && (
          <div className="h-32 bg-gray-700 rounded mb-4"></div>
        )}
        <div className="h-6 bg-gray-700 rounded w-3/4 mb-4"></div>
        <SkeletonText lines={2} />
      </div>
    </div>
  );
};
